import { useEffect, useState } from "react";
import axios from "axios";
import { useParams, useNavigate } from "react-router-dom";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { Helmet } from "react-helmet-async";
import {
    FaArrowLeft,
    FaArrowRight,
    FaCalendarAlt,
    FaCheck,
    FaChevronRight,
    FaFire,
    FaHome,
    FaInfoCircle,
    FaMapMarkerAlt,
    FaShieldAlt,
    FaTag,
    FaUsers,
    FaBed,
    FaExpand,
    FaTimes,
    FaCheckCircle,
    FaUser,
} from "react-icons/fa";

const ROOM_IMG = "https://uaediscountedhotels.com/uploads/rooms";

// ============================================================
// DESIGN COLORS
// ============================================================
// Primary Green: #0B5552
// Dark Green:    #073F3D
// Page Cream:    #F7F5EF
// Soft Green:    #E8F1EF
// Gold:          #C8A951
// ============================================================

export default function RoomDetail() {
    const { roomId } = useParams();
    const navigate = useNavigate();
    const { executeRecaptcha } = useGoogleReCaptcha();

    const [room, setRoom] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const [activeImage, setActiveImage] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [modalIndex, setModalIndex] = useState(0);
    const [showBookingModal, setShowBookingModal] = useState(false);

    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [checkIn, setCheckIn] = useState("");
    const [checkOut, setCheckOut] = useState("");
    const [guests, setGuests] = useState(1);
    const [bookingLoading, setBookingLoading] = useState(false);

    const today = new Date().toISOString().split("T")[0];
    const selectedDate = checkIn || today;

    useEffect(() => {
        document.body.style.overflow =
            showBookingModal || showModal ? "hidden" : "auto";

        return () => {
            document.body.style.overflow = "auto";
        };
    }, [showBookingModal, showModal]);

    useEffect(() => {
        const fetchRoom = async () => {
            try {
                setLoading(true);
                setError(false);

                const res = await axios.get(
                    `https://beachhoteldubai.com/api/rooms/${roomId}`
                );

                setRoom(res.data);
                setActiveImage(res.data?.image || null);
            } catch (err) {
                console.error(err);
                setError(true);
            } finally {
                setLoading(false);
            }
        };

        if (roomId) fetchRoom();
    }, [roomId]);

    // ============================================================
    // LOADING
    // ============================================================

    if (loading) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#F7F5EF]">
                <div className="text-center">
                    <div className="mx-auto h-12 w-12 animate-spin rounded-full border-4 border-[#0B5552]/15 border-t-[#0B5552]" />

                    <p className="mt-4 text-sm font-medium text-slate-500">
                        Loading room details...
                    </p>
                </div>
            </div>
        );
    }

    // ============================================================
    // ERROR
    // ============================================================

    if (error || !room) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[#F7F5EF] px-4">
                <div className="w-full max-w-md rounded-[28px] border border-[#0B5552]/10 bg-white p-8 text-center shadow-[0_20px_60px_rgba(7,63,61,0.10)]">
                    <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-red-50 text-red-500">
                        <FaInfoCircle className="text-2xl" />
                    </div>

                    <h2 className="mt-5 text-2xl font-bold text-[#073F3D]">
                        Room not found
                    </h2>

                    <p className="mt-2 text-sm leading-6 text-slate-500">
                        We could not load this room. Please return to the previous
                        page.
                    </p>

                    <button
                        onClick={() => navigate(-1)}
                        className="mt-6 inline-flex items-center gap-2 rounded-xl bg-[#0B5552] px-6 py-3 text-sm font-semibold text-white transition hover:bg-[#073F3D]"
                    >
                        <FaArrowLeft />
                        Go Back
                    </button>
                </div>
            </div>
        );
    }

    // ============================================================
    // PARSE IMAGES
    // ============================================================

    let images = [];

    try {
        const parsed = room.images
            ? typeof room.images === "string"
                ? JSON.parse(room.images)
                : room.images
            : [];

        images = Array.isArray(parsed) ? parsed : [];
    } catch {
        images = [];
    }

    // ============================================================
    // PARSE AMENITIES
    // ============================================================

    let amenities = [];

    try {
        const parsed = room.amenities
            ? typeof room.amenities === "string"
                ? JSON.parse(room.amenities)
                : room.amenities
            : [];

        amenities = Array.isArray(parsed)
            ? parsed
            : typeof parsed === "string"
                ? parsed
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean)
                : [];
    } catch {
        amenities =
            typeof room.amenities === "string"
                ? room.amenities
                    .split(",")
                    .map((item) => item.trim())
                    .filter(Boolean)
                : [];
    }

    const allImages = [...new Set([room.image, ...images].filter(Boolean))];

    const user = JSON.parse(localStorage.getItem("user") || "null");

    // ============================================================
    // PRICING
    // ============================================================

    const getNights = () => {
        if (!checkIn || !checkOut) return 1;

        const start = new Date(checkIn);
        const end = new Date(checkOut);

        const diffTime = end - start;

        const nights = Math.ceil(
            diffTime / (1000 * 60 * 60 * 24)
        );

        return nights > 0 ? nights : 1;
    };

    const getTourismFee = () => {
        return Number(room?.hotel?.rating) === 5 ? 20 : 10;
    };

    const activeSeason = room?.seasonal_prices?.find((season) => {
        const date = new Date(selectedDate);

        return (
            date >= new Date(season.start_date) &&
            date <= new Date(season.end_date)
        );
    });

    const nightlyRate = (() => {
        if (!room) return 0;

        let finalPrice = Number(room.price || 0);

        if (room.seasonal_prices?.length > 0) {
            const date = new Date(selectedDate);

            const match = room.seasonal_prices.find((season) => {
                const start = new Date(season.start_date);
                const end = new Date(season.end_date);

                return date >= start && date <= end;
            });

            if (match) {
                finalPrice = Number(
                    match.price || finalPrice
                );
            }
        }

        if (
            room.discount_price &&
            Number(room.discount_price) < finalPrice
        ) {
            finalPrice = Number(room.discount_price);
        }

        return finalPrice;
    })();

    const getFinalPriceBreakdown = () => {
        const base = nightlyRate;
        const nights = getNights();

        const serviceCharge = 0.1;
        const vat = 0.05;
        const tourismFee = getTourismFee();

        const nightlyWithTax =
            base +
            base * serviceCharge +
            base * vat +
            tourismFee;

        return {
            base,
            nights,
            serviceCharge,
            vat,
            tourismFee,
            nightlyWithTax,
            total: Math.round(
                nightlyWithTax * nights
            ),
        };
    };

    const roundPrice = (value) =>
        Math.round(Number(value || 0));

    const isDiscounted =
        room?.discount_price &&
        Number(room.discount_price) < Number(room.price);

    const savedAmount = isDiscounted
        ? Number(room.price) -
        Number(room.discount_price)
        : 0;

    const discountPercent = isDiscounted
        ? Math.round(
            ((Number(room.price) -
                Number(room.discount_price)) /
                Number(room.price)) *
            100
        )
        : 0;

    const isSeasonal = Boolean(
        activeSeason?.price
    );

    // ============================================================
    // GALLERY
    // ============================================================

    const openGallery = (index) => {
        setModalIndex(index);
        setActiveImage(allImages[index]);
        setShowModal(true);
    };

    const nextImage = () => {
        if (!allImages.length) return;

        setModalIndex((prev) => {
            const nextIndex =
                prev === allImages.length - 1
                    ? 0
                    : prev + 1;

            setActiveImage(
                allImages[nextIndex]
            );

            return nextIndex;
        });
    };

    const previousImage = () => {
        if (!allImages.length) return;

        setModalIndex((prev) => {
            const previousIndex =
                prev === 0
                    ? allImages.length - 1
                    : prev - 1;

            setActiveImage(
                allImages[previousIndex]
            );

            return previousIndex;
        });
    };

    // ============================================================
    // BOOKING
    // ============================================================

    const handleBooking = async () => {
        if (
            !name ||
            !email ||
            !phone ||
            !checkIn ||
            !checkOut
        ) {
            alert("Please fill all fields");
            return;
        }

        if (
            new Date(checkOut) <=
            new Date(checkIn)
        ) {
            alert(
                "Check-out must be after Check-in"
            );
            return;
        }

        if (!executeRecaptcha) {
            alert(
                "Captcha not ready. Refresh page."
            );
            return;
        }

        try {
            setBookingLoading(true);

            const token =
                await executeRecaptcha(
                    "room_booking"
                );

            const breakdown =
                getFinalPriceBreakdown();

            const bookingData = {
                user_id: user?.id,
                hotel_id:
                    room?.hotel?.id ??
                    room?.hotel_id,
                room_id: room.id,
                room_name:
                    room.name ||
                    `Room ${room.room_number}`,
                room_price: room.price,
                discount_price:
                    room.discount_price ||
                    null,
                name,
                email,
                phone,
                check_in: checkIn,
                check_out: checkOut,
                guests,
                season_name:
                    activeSeason?.season_name ||
                    null,
                season_start:
                    activeSeason?.start_date ||
                    null,
                season_end:
                    activeSeason?.end_date ||
                    null,
                total_amount:
                    breakdown.total,
                nights: breakdown.nights,
                captcha: token,
            };

            const res = await axios.post(
                "https://beachhoteldubai.com/api/hotel-payment",
                bookingData
            );

            if (res.data.payment_url) {
                window.location.href =
                    res.data.payment_url;
            } else {
                alert(
                    "Payment URL not received"
                );
            }
        } catch (err) {
            console.error(err);

            if (err.response) {
                alert(
                    `❌ ${err.response.data.error ||
                    err.response.data.message ||
                    "Booking failed"
                    }`
                );
            } else {
                alert("❌ Server error");
            }
        } finally {
            setBookingLoading(false);
        }
    };

    const finalNightly = roundPrice(
        getFinalPriceBreakdown()
            .nightlyWithTax
    );

    const stayTotal = roundPrice(
        getFinalPriceBreakdown().total
    );

    // ============================================================
    // RETURN
    // ============================================================

    return (
        <>
            <Helmet>
                <title>
                    {room.meta_title || room.name}
                </title>

                <meta
                    name="description"
                    content={
                        room.meta_description ||
                        room.description
                    }
                />

                <link
                    rel="canonical"
                    href={`https://beachhoteldubai.com/hotels/${room.hotel?.slug}/${room.id}`}
                />

                <meta
                    property="og:title"
                    content={
                        room.meta_title ||
                        room.name
                    }
                />

                <meta
                    property="og:description"
                    content={
                        room.meta_description ||
                        room.description
                    }
                />

                <meta
                    property="og:url"
                    content={`https://beachhoteldubai.com/hotels/${room.hotel?.slug}/${room.id}`}
                />

                <meta
                    property="og:image"
                    content={
                        room?.image
                            ? `${ROOM_IMG}/${room.image}`
                            : "https://beachhoteldubai.com/images/default-og.png"
                    }
                />

                <meta
                    property="og:type"
                    content="website"
                />

                <meta
                    name="twitter:card"
                    content="summary_large_image"
                />

                <meta
                    name="twitter:title"
                    content={
                        room.meta_title ||
                        room.name
                    }
                />

                <meta
                    name="twitter:description"
                    content={
                        room.meta_description ||
                        room.description
                    }
                />

                <meta
                    name="twitter:image"
                    content={
                        room?.image
                            ? `${ROOM_IMG}/${room.image}`
                            : "https://beachhoteldubai.com/images/default-og.png"
                    }
                />

                <meta
                    name="robots"
                    content="index, follow"
                />
            </Helmet>

            <main className="min-h-screen bg-[#F7F5EF] text-[#173A38] pb-20 lg:pb-0 mt-24">

                {/* =========================================================
                    ROOM HERO
                ========================================================== */}

                <section className="relative w-full overflow-hidden bg-[#073F3D]">

                    <div className="relative h-[400px] sm:h-[450px] md:h-[500px] lg:h-[550px] xl:h-[590px]">

                        {activeImage && (
                            <img
                                src={`${ROOM_IMG}/${activeImage}`}
                                alt={
                                    room.name ||
                                    "Room"
                                }
                                className="absolute inset-0 h-full w-full object-cover object-center"
                            />
                        )}

                        {/* Premium Green Overlay */}

                        <div className="absolute inset-0 bg-gradient-to-r from-[#073F3D]/95 via-[#073F3D]/55 to-black/10" />

                        <div className="absolute inset-x-0 bottom-0 h-72 bg-gradient-to-t from-[#073F3D]/95 via-[#073F3D]/55 to-transparent" />

                        <div className="absolute inset-x-0 top-0 h-36 bg-gradient-to-b from-[#073F3D]/70 to-transparent" />

                        {/* =================================================
                            TOP NAVIGATION
                        ================================================== */}

                        <div className="absolute left-0 right-0 top-0 z-10">

                            <div className="mx-auto mt-[72px] flex max-w-[1440px] items-center justify-between px-5 py-5 sm:px-8 lg:px-12 xl:px-16">

                                <div className="flex min-w-0 items-center gap-2 text-sm text-white/85">

                                    <button
                                        onClick={() =>
                                            navigate("/")
                                        }
                                        className="inline-flex items-center gap-2 whitespace-nowrap transition hover:text-[#D8C27A]"
                                    >
                                        <FaHome />

                                        <span className="hidden sm:inline">
                                            Home
                                        </span>
                                    </button>

                                    <FaChevronRight className="shrink-0 text-[9px] text-white/40" />

                                    <button
                                        onClick={() => {
                                            if (
                                                room.hotel
                                                    ?.slug
                                            ) {
                                                navigate(
                                                    `/hotels/${room.hotel.slug}`
                                                );
                                            }
                                        }}
                                        className="max-w-[140px] truncate transition hover:text-[#D8C27A] sm:max-w-[220px]"
                                    >
                                        {room.hotel?.name ||
                                            "Hotel"}
                                    </button>

                                    <FaChevronRight className="shrink-0 text-[9px] text-white/40" />

                                    <span className="max-w-[120px] truncate font-semibold text-white sm:max-w-[180px]">
                                        Room{" "}
                                        {
                                            room.room_number
                                        }
                                    </span>
                                </div>

                                <button
                                    onClick={() =>
                                        navigate(-1)
                                    }
                                    className="inline-flex shrink-0 items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-semibold text-white backdrop-blur-md transition hover:border-white/30 hover:bg-white/20"
                                >
                                    <FaArrowLeft />

                                    <span className="hidden sm:inline">
                                        Back
                                    </span>
                                </button>

                            </div>
                        </div>

                        {/* =================================================
                            HERO CONTENT
                        ================================================== */}

                        <div className="absolute inset-x-0 bottom-0 z-10">

                            <div className="mx-auto max-w-[1440px] px-5 pb-8 sm:px-8 sm:pb-12 lg:px-12 lg:pb-16 xl:px-16">

                                <div className="max-w-4xl">

                                    <div className="flex flex-wrap items-center gap-2">

                                        {room.hotel?.rating && (
                                            <span className="rounded-full bg-[#D8C27A] px-3.5 py-1.5 text-xs font-bold text-[#173A38] shadow-sm">
                                                ★{" "}
                                                {
                                                    room.hotel
                                                        .rating
                                                }{" "}
                                                Star Hotel
                                            </span>
                                        )}

                                        {isSeasonal && (
                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[#C66B3D]/90 px-3.5 py-1.5 text-xs font-bold text-white">
                                                <FaFire />

                                                Limited Time
                                                Offer
                                            </span>
                                        )}

                                        {isDiscounted && (
                                            <span className="inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-[#0B5552] px-3.5 py-1.5 text-xs font-bold text-white">
                                                <FaTag />

                                                {discountPercent}
                                                % OFF
                                            </span>
                                        )}

                                    </div>

                                    <h1 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-white sm:text-4xl md:text-5xl lg:text-6xl">
                                        {room.name ||
                                            `Room ${room.room_number}`}
                                    </h1>

                                    <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-white/80">

                                        <span className="inline-flex items-center gap-2">
                                            <FaMapMarkerAlt className="text-[#D8C27A]" />

                                            {room.hotel
                                                ?.name ||
                                                "Dubai Hotel"}
                                        </span>

                                        <span className="inline-flex items-center gap-2">
                                            <FaUsers className="text-[#D8C27A]" />

                                            Up to{" "}
                                            {room.capacity ||
                                                "-"}{" "}
                                            guests
                                        </span>

                                        <span className="inline-flex items-center gap-2">
                                            <FaBed className="text-[#D8C27A]" />

                                            {room.bed_type ||
                                                "Comfortable bedding"}
                                        </span>

                                        {room.size_sqm && (
                                            <span className="inline-flex items-center gap-2">
                                                <FaExpand className="text-[#D8C27A]" />

                                                {
                                                    room.size_sqm
                                                }{" "}
                                                m²
                                            </span>
                                        )}

                                    </div>

                                </div>
                            </div>
                        </div>

                        {/* =================================================
                            HERO GALLERY ARROWS
                        ================================================== */}

                        {allImages.length > 1 && (
                            <>
                                <button
                                    onClick={
                                        previousImage
                                    }
                                    className="absolute left-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-[#073F3D]/40 text-white backdrop-blur-md transition hover:bg-[#073F3D]/70 sm:left-6"
                                    aria-label="Previous image"
                                >
                                    <FaArrowLeft />
                                </button>

                                <button
                                    onClick={nextImage}
                                    className="absolute right-4 top-1/2 z-20 flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border border-white/20 bg-[#073F3D]/40 text-white backdrop-blur-md transition hover:bg-[#073F3D]/70 sm:right-6"
                                    aria-label="Next image"
                                >
                                    <FaArrowRight />
                                </button>
                            </>
                        )}

                        {/* =================================================
                            THUMBNAILS
                        ================================================== */}

                        {allImages.length > 1 && (
                            <div className="absolute bottom-5 left-1/2 z-30 hidden -translate-x-1/2 gap-2 md:flex">

                                {allImages
                                    .slice(0, 6)
                                    .map(
                                        (
                                            img,
                                            index
                                        ) => (
                                            <button
                                                key={`${img}-${index}`}
                                                onClick={() => {
                                                    setActiveImage(
                                                        img
                                                    );
                                                    setModalIndex(
                                                        index
                                                    );
                                                }}
                                                className={`h-12 w-16 overflow-hidden rounded-xl border-2 transition ${activeImage ===
                                                    img
                                                    ? "scale-105 border-[#D8C27A]"
                                                    : "border-white/30 opacity-70 hover:opacity-100"
                                                    }`}
                                            >
                                                <img
                                                    src={`${ROOM_IMG}/${img}`}
                                                    alt={`Room ${index + 1}`}
                                                    className="h-full w-full object-cover"
                                                />
                                            </button>
                                        )
                                    )}

                            </div>
                        )}

                    </div>
                </section>

                {/* =========================================================
                    QUICK ROOM INFO
                ========================================================== */}

                <section className="relative z-20 -mt-1 md:-mt-10">

                    <div className="mx-auto max-w-[1440px] px-5 sm:px-8 lg:px-12 xl:px-16">

                        <div className="grid overflow-hidden rounded-[24px] border border-[#0B5552]/10 bg-white shadow-[0_18px_50px_rgba(7,63,61,0.10)] sm:grid-cols-2 lg:grid-cols-4">

                            {/* Guests */}

                            <div className="flex items-center gap-4 border-b border-[#0B5552]/10 p-5 sm:border-r lg:border-b-0">

                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#E8F1EF] text-[#8B0000]">
                                    <FaUsers />
                                </div>

                                <div>
                                    <p className="text-xs font-medium text-slate-400">
                                        Guests
                                    </p>

                                    <p className="mt-1 font-bold text-[#173A38]">
                                        {room.capacity ||
                                            "-"}{" "}
                                        Guests
                                    </p>
                                </div>

                            </div>

                            {/* Bed */}

                            <div className="flex items-center gap-4 border-b border-[#0B5552]/10 p-5 lg:border-b-0 lg:border-r">

                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#E8F1EF] text-[#8B0000]">
                                    <FaBed />
                                </div>

                                <div>
                                    <p className="text-xs font-medium text-slate-400">
                                        Bed Type
                                    </p>

                                    <p className="mt-1 font-bold text-[#173A38]">
                                        {room.bed_type ||
                                            "-"}
                                    </p>
                                </div>

                            </div>

                            {/* Size */}

                            <div className="flex items-center gap-4 border-b border-[#0B5552]/10 p-5 sm:border-r sm:border-b-0">

                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#F4EDDA] text-[#A38332]">
                                    <FaExpand />
                                </div>

                                <div>
                                    <p className="text-xs font-medium text-slate-400">
                                        Room Size
                                    </p>

                                    <p className="mt-1 font-bold text-[#173A38]">
                                        {room.size_sqm
                                            ? `${room.size_sqm} m²`
                                            : "-"}
                                    </p>
                                </div>

                            </div>

                            {/* Availability */}

                            <div className="flex items-center gap-4 p-5">

                                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-[#E8F1EF] text-[#8B0000]">
                                    <FaShieldAlt />
                                </div>

                                <div>
                                    <p className="text-xs font-medium text-slate-400">
                                        Availability
                                    </p>

                                    <p
                                        className={`mt-1 font-bold ${room.is_available ===
                                            false
                                            ? "text-red-500"
                                            : "text-[#0B755F]"
                                            }`}
                                    >
                                        {room.is_available ===
                                            false
                                            ? "Unavailable"
                                            : "Available to Book"}
                                    </p>
                                </div>

                            </div>

                        </div>
                    </div>
                </section>

                {/* =========================================================
                    MAIN CONTENT
                ========================================================== */}

                <section className="mx-auto max-w-[1440px] px-5 py-10 sm:px-8 sm:py-14 lg:px-12 lg:py-16 xl:px-16">

                    <div className="grid items-start gap-8 lg:grid-cols-[minmax(0,1fr)_390px]">

                        {/* =================================================
                            LEFT CONTENT
                        ================================================== */}

                        <div className="space-y-7">

                            {/* =================================================
                                INTRO
                            ================================================== */}

                            <div className="rounded-[28px] border border-[#0B5552]/10 bg-white p-6 shadow-[0_10px_35px_rgba(7,63,61,0.05)] sm:p-8">

                                <div className="flex flex-wrap items-start justify-between gap-5">

                                    <div>

                                        <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8B0000]">
                                            Room{" "}
                                            {
                                                room.room_number
                                            }
                                        </p>

                                        <h2 className="mt-2 text-2xl font-extrabold tracking-tight text-[#173A38] sm:text-3xl">
                                            {room.name ||
                                                "Comfortable Dubai Stay"}
                                        </h2>

                                        <p className="mt-2 flex items-center gap-2 text-sm text-slate-500">
                                            <FaMapMarkerAlt className="text-[#8B0000]" />

                                            {room.hotel
                                                ?.name ||
                                                "Dubai Hotel"}
                                        </p>

                                    </div>

                                    <div className="rounded-2xl bg-[#E8F1EF] px-5 py-4 text-right">

                                        <p className="text-xs text-slate-400">
                                            From
                                        </p>

                                        <p className="text-xl font-extrabold text-[#8B0000]">
                                            {finalNightly.toLocaleString()}{" "}
                                            AED
                                        </p>

                                        <p className="text-[11px] text-slate-400">
                                            per night incl. fees
                                        </p>

                                    </div>

                                </div>

                                <div
                                    className="room-content prose prose-slate mt-7 max-w-none text-sm leading-7"
                                    dangerouslySetInnerHTML={{
                                        __html:
                                            room.description ||
                                            "<p>Enjoy a comfortable stay with modern facilities and convenient access to Dubai.</p>",
                                    }}
                                />

                            </div>

                            {/* =================================================
                                GALLERY
                            ================================================== */}

                            {allImages.length > 0 && (
                                <div className="rounded-[28px] border border-[#0B5552]/10 bg-white p-6 shadow-[0_10px_35px_rgba(7,63,61,0.05)] sm:p-8">

                                    <div className="flex items-end justify-between gap-4">

                                        <div>

                                            <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8B0000]">
                                                Explore the room
                                            </p>

                                            <h2 className="mt-1 text-2xl font-extrabold text-[#173A38]">
                                                Room Gallery
                                            </h2>

                                        </div>

                                        <span className="hidden rounded-full bg-[#E8F1EF] px-3.5 py-1.5 text-xs font-semibold text-[#8B0000] sm:inline-flex">
                                            {allImages.length}{" "}
                                            Photos
                                        </span>

                                    </div>

                                    <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3">

                                        {allImages.map(
                                            (
                                                img,
                                                index
                                            ) => (
                                                <button
                                                    key={`${img}-${index}`}
                                                    onClick={() =>
                                                        openGallery(
                                                            index
                                                        )
                                                    }
                                                    className={`group relative overflow-hidden rounded-2xl ${index ===
                                                        0
                                                        ? "col-span-2 row-span-2 h-64 sm:h-80"
                                                        : "h-32 sm:h-40"
                                                        }`}
                                                >

                                                    <img
                                                        src={`${ROOM_IMG}/${img}`}
                                                        alt={`${room.name ||
                                                            "Room"
                                                            } ${index +
                                                            1}`}
                                                        className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                                                    />

                                                    <div className="absolute inset-0 bg-gradient-to-t from-[#073F3D]/70 via-transparent to-transparent opacity-80" />

                                                    <div className="absolute bottom-3 left-3 flex items-center gap-2 text-xs font-semibold text-white">
                                                        <FaExpand />

                                                        View photo
                                                    </div>

                                                </button>
                                            )
                                        )}

                                    </div>
                                </div>
                            )}

                            {/* =================================================
                                AMENITIES
                            ================================================== */}

                            <div className="rounded-[28px] border border-[#0B5552]/10 bg-white p-6 shadow-[0_10px_35px_rgba(7,63,61,0.05)] sm:p-8">

                                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8B0000]">
                                    Comfort & convenience
                                </p>

                                <h2 className="mt-1 text-2xl font-extrabold text-[#173A38]">
                                    Room Amenities
                                </h2>

                                {amenities.length > 0 ? (
                                    <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">

                                        {amenities.map(
                                            (
                                                amenity,
                                                index
                                            ) => (
                                                <div
                                                    key={`${amenity}-${index}`}
                                                    className="flex items-center gap-3 rounded-2xl border border-[#0B5552]/10 bg-[#F8FAF7] px-4 py-3.5"
                                                >

                                                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#E8F1EF] text-[#8B0000]">
                                                        <FaCheck className="text-xs" />
                                                    </span>

                                                    <span className="text-sm font-medium text-slate-700">
                                                        {
                                                            amenity
                                                        }
                                                    </span>

                                                </div>
                                            )
                                        )}

                                    </div>
                                ) : (
                                    <p className="mt-5 text-sm text-slate-400">
                                        No amenities listed for this room.
                                    </p>
                                )}

                            </div>

                            {/* =================================================
                                POLICIES
                            ================================================== */}

                            <div className="rounded-[28px] border border-[#0B5552]/10 bg-white p-6 shadow-[0_10px_35px_rgba(7,63,61,0.05)] sm:p-8">

                                <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#8B0000]">
                                    Before you book
                                </p>

                                <h2 className="mt-1 text-2xl font-extrabold text-[#173A38]">
                                    Room Policies
                                </h2>

                                <div className="mt-6 grid gap-3 sm:grid-cols-2">

                                    <div className="rounded-2xl bg-[#F8FAF7] p-4">
                                        <p className="text-xs font-medium text-slate-400">
                                            Cancellation
                                        </p>

                                        <p className="mt-1 text-sm font-semibold text-slate-700">
                                            {room.cancellation_policy ||
                                                "Policy applies"}
                                        </p>
                                    </div>

                                    <div className="rounded-2xl bg-[#F8FAF7] p-4">
                                        <p className="text-xs font-medium text-slate-400">
                                            Breakfast
                                        </p>

                                        <p className="mt-1 text-sm font-semibold text-slate-700">
                                            {room.breakfast_included
                                                ? "Included"
                                                : "Not Included"}
                                        </p>
                                    </div>

                                    <div className="rounded-2xl bg-[#F8FAF7] p-4">
                                        <p className="text-xs font-medium text-slate-400">
                                            Smoking
                                        </p>

                                        <p className="mt-1 text-sm font-semibold text-slate-700">
                                            {room.smoking_allowed
                                                ? "Allowed"
                                                : "Not Allowed"}
                                        </p>
                                    </div>

                                    <div className="rounded-2xl bg-[#F8FAF7] p-4">
                                        <p className="text-xs font-medium text-slate-400">
                                            Availability
                                        </p>

                                        <p
                                            className={`mt-1 text-sm font-semibold ${room.is_available ===
                                                false
                                                ? "text-red-500"
                                                : "text-[#0B755F]"
                                                }`}
                                        >
                                            {room.is_available ===
                                                false
                                                ? "Unavailable"
                                                : "Available"}
                                        </p>
                                    </div>

                                </div>
                            </div>

                            {/* =================================================
                                WHY THIS ROOM
                            ================================================== */}

                            <div className="relative overflow-hidden rounded-[28px] bg-[#073F3D] p-7 text-white shadow-[0_15px_45px_rgba(7,63,61,0.12)] sm:p-9">

                                <div className="absolute -right-20 -top-20 h-48 w-48 rounded-full bg-white/5" />

                                <div className="relative max-w-2xl">

                                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#D8C27A]">
                                        A better stay
                                    </p>

                                    <h2 className="!mt-2 !text-white text-2xl font-extrabold sm:text-3xl">
                                        Everything you need for a comfortable Dubai stay.
                                    </h2>

                                    <p className="mt-3 text-sm leading-6 text-white/70">
                                        Enjoy a carefully selected room
                                        with comfortable accommodation,
                                        convenient facilities and
                                        transparent pricing.
                                    </p>

                                    <div className="mt-6 grid gap-3 sm:grid-cols-3">

                                        {[
                                            "Great location",
                                            "Transparent pricing",
                                            "Booking support",
                                        ].map(
                                            (item) => (
                                                <div
                                                    key={item}
                                                    className="flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-3 text-xs font-semibold"
                                                >
                                                    <FaCheck className="text-[#D8C27A]" />

                                                    {item}
                                                </div>
                                            )
                                        )}

                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* =================================================
                            DESKTOP BOOKING CARD
                        ================================================== */}

                        <aside className="lg:sticky lg:top-24">
                            <div className="overflow-hidden rounded-[24px] border border-[#0B5552]/10 bg-white shadow-[0_16px_50px_rgba(7,63,61,0.10)]">

                                {/* Header */}
                                <div className="relative overflow-hidden bg-[#073F3D] px-6 py-7 text-white">
                                    <div className="absolute -right-10 -top-12 h-36 w-36 rounded-full bg-white/[0.04]" />
                                    <div className="absolute -bottom-16 right-16 h-36 w-36 rounded-full bg-white/[0.04]" />

                                    <div className="relative flex items-start justify-between gap-4">
                                        <div>
                                            <div className="mb-3 flex items-center gap-2">
                                                <span className="h-1.5 w-1.5 rounded-full bg-[#D6B779]" />
                                                <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60">
                                                    Room Reservation
                                                </p>
                                            </div>

                                            <h3 className="!mt-0 !text-white text-xl font-bold leading-snug">
                                                Your Dubai stay starts here
                                            </h3>

                                            <p className="mt-1.5 text-xs text-white/55">
                                                Comfort, convenience and a memorable stay.
                                            </p>
                                        </div>

                                        <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-white/10 bg-white/10 text-[#D6B779]">
                                            <FaShieldAlt size={18} />
                                        </div>
                                    </div>

                                    <div className="relative mt-6 flex items-end gap-2">
                                        <span className="text-4xl font-extrabold tracking-tight">
                                            {finalNightly.toLocaleString()}
                                        </span>

                                        <span className="mb-1.5 text-xs font-medium text-white/60">
                                            AED / night
                                        </span>
                                    </div>

                                    <div className="relative mt-3 flex items-center gap-2 text-[11px] text-white/60">
                                        <FaCheckCircle className="text-[#D6B779]" />
                                        <span>Transparent pricing for your stay</span>
                                    </div>
                                </div>

                                <div className="p-5 sm:p-6">

                                    {/* Discount */}
                                    {isDiscounted && (
                                        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-emerald-100 bg-emerald-50/80 px-4 py-3">
                                            <div className="flex items-center gap-2">
                                                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                                                    <FaTag size={13} />
                                                </div>

                                                <div>
                                                    <p className="text-sm font-extrabold text-emerald-800">
                                                        {discountPercent}% OFF
                                                    </p>
                                                    <p className="text-[10px] text-emerald-700/70">
                                                        Special room discount
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="text-right">
                                                <p className="text-[10px] font-medium text-emerald-700/70">
                                                    You save
                                                </p>
                                                <p className="text-sm font-bold text-emerald-800">
                                                    {savedAmount.toLocaleString()} AED
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Seasonal */}
                                    {isSeasonal && (
                                        <div className="mb-4 flex gap-3 rounded-xl border border-orange-100 bg-[#FFF8F2] p-4">
                                            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-[#B85C2B]">
                                                <FaFire size={15} />
                                            </div>

                                            <div>
                                                <p className="text-sm font-bold text-[#A94F27]">
                                                    Seasonal offer active
                                                </p>

                                                <p className="mt-1 text-xs leading-5 text-[#A94F27]/75">
                                                    Dynamic pricing may change based on demand and availability.
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    {/* Divider */}
                                    <div className="my-5 border-t border-dashed border-slate-200" />

                                    {/* Price Breakdown */}
                                    <div>
                                        <div className="mb-4 flex items-center justify-between">
                                            <h4 className="text-sm font-bold text-[#173A38]">
                                                Price breakdown
                                            </h4>

                                            <span className="rounded-full bg-[#F4F7F5] px-2.5 py-1 text-[10px] font-semibold text-[#0B5552]">
                                                Per night
                                            </span>
                                        </div>

                                        <div className="space-y-4 text-[13px]">
                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-slate-500">
                                                    Room price / night
                                                </span>

                                                <span className="font-semibold text-slate-700">
                                                    {nightlyRate.toLocaleString()} AED
                                                </span>
                                            </div>

                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-slate-500">
                                                    Service charge (10%)
                                                </span>

                                                <span className="font-medium text-slate-700">
                                                    {roundPrice(nightlyRate * 0.1).toLocaleString()} AED
                                                </span>
                                            </div>

                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-slate-500">
                                                    VAT (5%)
                                                </span>

                                                <span className="font-medium text-slate-700">
                                                    {roundPrice(nightlyRate * 0.05).toLocaleString()} AED
                                                </span>
                                            </div>

                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-slate-500">
                                                    Tourism fee
                                                    {Number(room?.hotel?.rating) === 5 && " (5-Star)"}
                                                </span>

                                                <span className="font-medium text-slate-700">
                                                    {getTourismFee()} AED
                                                </span>
                                            </div>
                                        </div>
                                    </div>

                                    {/* Final Price */}
                                    <div className="mt-6 rounded-2xl border border-[#0B5552]/10 bg-[#F2F7F5] p-4">
                                        <div className="flex items-center justify-between gap-3">
                                            <div>
                                                <p className="text-xs font-semibold text-slate-500">
                                                    Final price / night
                                                </p>

                                                <p className="mt-1 text-[10px] text-slate-400">
                                                    Including applicable charges
                                                </p>
                                            </div>

                                            <div className="text-right">
                                                <p className="text-xl font-extrabold tracking-tight text-[#0B5552]">
                                                    {finalNightly.toLocaleString()} AED
                                                </p>
                                            </div>
                                        </div>

                                        {checkIn && checkOut && (
                                            <div className="mt-4 flex items-center justify-between border-t border-[#0B5552]/10 pt-4">
                                                <div>
                                                    <p className="text-xs font-semibold text-[#173A38]">
                                                        Total stay
                                                    </p>

                                                    <p className="mt-1 text-[10px] text-slate-500">
                                                        {getNights()} night(s)
                                                    </p>
                                                </div>

                                                <p className="text-lg font-extrabold text-[#0B5552]">
                                                    {stayTotal.toLocaleString()} AED
                                                </p>
                                            </div>
                                        )}
                                    </div>

                                    {/* Reserve Button */}
                                    <button
                                        onClick={() => setShowBookingModal(true)}
                                        disabled={room.is_available === false}
                                        className={`mt-5 flex w-full items-center justify-center gap-2 rounded-xl py-4 text-sm font-bold transition-all duration-300 ${room.is_available === false
                                            ? "cursor-not-allowed bg-slate-200 text-slate-400 shadow-none"
                                            : "bg-[#0B5552] text-white shadow-[0_8px_20px_rgba(11,85,82,0.20)] hover:-translate-y-0.5 hover:bg-[#073F3D] hover:shadow-[0_12px_26px_rgba(11,85,82,0.25)]"
                                            }`}
                                    >
                                        {room.is_available === false
                                            ? "Room Unavailable"
                                            : "Reserve This Room"}

                                        {room.is_available !== false && (
                                            <FaArrowRight size={13} />
                                        )}
                                    </button>

                                    {/* Security Note */}
                                    <div className="mt-5 flex items-center justify-center gap-2 text-center text-[10px] leading-5 text-slate-400">
                                        <FaShieldAlt className="shrink-0 text-[#0B755F]" />

                                        <span>
                                            Secure booking • No charge until checkout/payment
                                        </span>
                                    </div>

                                    {/* Cancellation Policy */}
                                    <div className="mt-3 flex items-center justify-center gap-2 border-t border-slate-100 pt-3">
                                        <FaCheckCircle
                                            className={
                                                room.cancellation_policy === "free"
                                                    ? "text-emerald-600"
                                                    : "text-slate-400"
                                            }
                                            size={12}
                                        />

                                        <p className="text-center text-[11px] font-medium text-slate-500">
                                            {room.cancellation_policy === "free"
                                                ? "Free cancellation available"
                                                : "Cancellation policy applies"}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </aside>

                    </div>
                </section>

                {/* =========================================================
                    MOBILE BOOKING BAR
                ========================================================== */}

                <div className="fixed inset-x-0 bottom-0 z-40 border-t border-[#0B5552]/10 bg-white/95 p-3 shadow-[0_-8px_30px_rgba(7,63,61,0.12)] backdrop-blur-md lg:hidden">

                    <div className="mx-auto flex max-w-[1440px] items-center gap-3">

                        <div className="min-w-0 flex-1">

                            <p className="truncate text-xs font-medium text-slate-400">
                                From
                            </p>

                            <p className="text-lg font-extrabold text-[#8B0000]">

                                {finalNightly.toLocaleString()}{" "}
                                AED

                                <span className="ml-1 text-[10px] font-medium text-slate-400">
                                    / night
                                </span>

                            </p>

                        </div>

                        <button
                            onClick={() =>
                                setShowBookingModal(
                                    true
                                )
                            }
                            disabled={
                                room.is_available ===
                                false
                            }
                            className="rounded-xl bg-[#0B5552] px-5 py-3 text-xs font-bold text-white shadow-lg transition hover:bg-[#073F3D] disabled:bg-slate-300"
                        >
                            {room.is_available ===
                                false
                                ? "Unavailable"
                                : "Reserve Now"}
                        </button>

                    </div>
                </div>

            </main>

            {/* =============================================================
                FULLSCREEN GALLERY MODAL
            ============================================================== */}

            {showModal &&
                allImages.length > 0 && (
                    <div className="fixed inset-0 z-[100] bg-[#061F1E]/95">

                        <button
                            onClick={() =>
                                setShowModal(
                                    false
                                )
                            }
                            className="absolute right-10 top-40 z-20 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white transition hover:bg-white/20"
                            aria-label="Close gallery"
                        >
                            <FaTimes />
                        </button>

                        {allImages.length > 1 && (
                            <>
                                <button
                                    onClick={
                                        previousImage
                                    }
                                    className="absolute left-4 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition hover:bg-white/20 sm:left-8"
                                >
                                    <FaArrowLeft />
                                </button>

                                <button
                                    onClick={
                                        nextImage
                                    }
                                    className="absolute right-4 top-1/2 z-20 flex h-12 w-12 -translate-y-1/2 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur-md transition hover:bg-white/20 sm:right-8"
                                >
                                    <FaArrowRight />
                                </button>
                            </>
                        )}

                        <div className="flex h-full items-center justify-center p-6 sm:p-12">

                            <img
                                src={`${ROOM_IMG}/${allImages[modalIndex]}`}
                                alt={
                                    room.name ||
                                    "Room"
                                }
                                className="max-h-[82vh] max-w-[92vw] rounded-2xl object-contain shadow-2xl"
                            />

                        </div>

                        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-4 py-2 text-xs font-semibold text-white backdrop-blur-md">
                            {modalIndex + 1} /{" "}
                            {allImages.length}
                        </div>

                    </div>
                )}

            {/* =============================================================
                BOOKING MODAL
            ============================================================== */}

            {showBookingModal && (
                <div className="fixed inset-0 z-[9999] overflow-y-auto bg-[#061F1E]/75 px-3 py-5 backdrop-blur-md sm:px-5 sm:py-8">

                    <div className="flex min-h-full items-center justify-center">

                        <div className="relative my-auto w-full max-w-2xl overflow-hidden rounded-[24px] border border-white/20 bg-white shadow-[0_25px_80px_rgba(0,0,0,0.25)] sm:rounded-[28px]">

                            {/* Close Button */}
                            <button
                                onClick={() => setShowBookingModal(false)}
                                aria-label="Close booking modal"
                                className="absolute right-4 top-4 z-20 flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/10 text-white transition hover:rotate-90 hover:bg-white/20 sm:right-6 sm:top-6"
                            >
                                <FaTimes size={15} />
                            </button>

                            {/* Modal Header */}
                            <div className="relative overflow-hidden bg-[#073F3D] px-5 py-7 text-white sm:px-8 sm:py-8">

                                <div className="absolute -right-12 -top-16 h-48 w-48 rounded-full bg-white/[0.035]" />
                                <div className="absolute -bottom-24 right-24 h-48 w-48 rounded-full bg-white/[0.035]" />

                                <div className="relative">

                                    <div className="mb-4 flex items-center gap-2">
                                        <span className="h-1.5 w-1.5 rounded-full bg-[#D6B779]" />

                                        <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-white/60">
                                            Complete your reservation
                                        </p>
                                    </div>

                                    <h3 className="!mt-0 !text-white pr-12 text-2xl font-extrabold leading-tight sm:text-3xl">
                                        Book{" "}
                                        {room.name || `Room ${room.room_number}`}
                                    </h3>

                                    <p className="mt-2 max-w-md text-xs leading-5 text-white/60">
                                        Enter your details below to continue with your hotel reservation.
                                    </p>

                                    <div className="mt-5 flex flex-wrap items-center gap-3">

                                        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2">
                                            <FaShieldAlt className="text-[#D6B779]" size={12} />

                                            <span className="text-[10px] font-semibold text-white/80">
                                                Secure reservation
                                            </span>
                                        </div>

                                        <div className="flex items-center gap-2 rounded-full border border-white/10 bg-white/10 px-3 py-2">
                                            <FaCheckCircle className="text-[#D6B779]" size={12} />

                                            <span className="text-[10px] font-semibold text-white/80">
                                                Transparent pricing
                                            </span>
                                        </div>

                                    </div>
                                </div>
                            </div>

                            {/* Modal Body */}
                            <div className="max-h-[calc(100vh-190px)] overflow-y-auto p-5 sm:p-8">

                                {/* Guest Details */}
                                <div className="mb-5">

                                    <div className="mb-4 flex items-center gap-3">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#E8F1EF] text-[#0B5552]">
                                            <FaUser size={14} />
                                        </div>

                                        <div>
                                            <h4 className="text-sm font-bold text-[#173A38]">
                                                Guest Information
                                            </h4>

                                            <p className="mt-0.5 text-[11px] text-slate-400">
                                                Please provide your contact details.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid gap-4 sm:grid-cols-2">

                                        {/* Name */}
                                        <div>
                                            <label className="mb-2 block text-xs font-semibold text-[#173A38]">
                                                Full Name
                                            </label>

                                            <input
                                                type="text"
                                                value={name}
                                                onChange={(e) => setName(e.target.value)}
                                                placeholder="Enter your full name"
                                                autoComplete="name"
                                                className="h-12 w-full rounded-xl border border-slate-200 bg-[#F8FAF8] px-4 text-sm text-[#173A38] outline-none transition placeholder:text-slate-400 hover:border-[#0B5552]/30 focus:border-[#0B5552] focus:bg-white focus:ring-4 focus:ring-[#0B5552]/[0.07]"
                                            />
                                        </div>

                                        {/* Email */}
                                        <div>
                                            <label className="mb-2 block text-xs font-semibold text-[#173A38]">
                                                Email Address
                                            </label>

                                            <input
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                placeholder="you@example.com"
                                                autoComplete="email"
                                                className="h-12 w-full rounded-xl border border-slate-200 bg-[#F8FAF8] px-4 text-sm text-[#173A38] outline-none transition placeholder:text-slate-400 hover:border-[#0B5552]/30 focus:border-[#0B5552] focus:bg-white focus:ring-4 focus:ring-[#0B5552]/[0.07]"
                                            />
                                        </div>

                                        {/* Phone */}
                                        <div>
                                            <label className="mb-2 block text-xs font-semibold text-[#173A38]">
                                                Phone Number
                                            </label>

                                            <input
                                                type="tel"
                                                value={phone}
                                                onChange={(e) => setPhone(e.target.value)}
                                                placeholder="+971 50 000 0000"
                                                autoComplete="tel"
                                                className="h-12 w-full rounded-xl border border-slate-200 bg-[#F8FAF8] px-4 text-sm text-[#173A38] outline-none transition placeholder:text-slate-400 hover:border-[#0B5552]/30 focus:border-[#0B5552] focus:bg-white focus:ring-4 focus:ring-[#0B5552]/[0.07]"
                                            />
                                        </div>

                                        {/* Guests */}
                                        <div>
                                            <label className="mb-2 block text-xs font-semibold text-[#173A38]">
                                                Number of Guests
                                            </label>

                                            <input
                                                type="number"
                                                min="1"
                                                max={room.capacity || undefined}
                                                value={guests}
                                                onChange={(e) => setGuests(e.target.value)}
                                                className="h-12 w-full rounded-xl border border-slate-200 bg-[#F8FAF8] px-4 text-sm text-[#173A38] outline-none transition hover:border-[#0B5552]/30 focus:border-[#0B5552] focus:bg-white focus:ring-4 focus:ring-[#0B5552]/[0.07]"
                                            />
                                        </div>

                                    </div>
                                </div>

                                {/* Stay Dates */}
                                <div className="mb-6 border-t border-slate-100 pt-5">

                                    <div className="mb-4 flex items-center gap-3">
                                        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#E8F1EF] text-[#0B5552]">
                                            <FaCalendarAlt size={14} />
                                        </div>

                                        <div>
                                            <h4 className="text-sm font-bold text-[#173A38]">
                                                Your Stay
                                            </h4>

                                            <p className="mt-0.5 text-[11px] text-slate-400">
                                                Select your arrival and departure dates.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="grid gap-4 sm:grid-cols-2">

                                        {/* Check-in */}
                                        <div>
                                            <label className="mb-2 block text-xs font-semibold text-[#173A38]">
                                                Check-in Date
                                            </label>

                                            <input
                                                type="date"
                                                value={checkIn}
                                                min={today}
                                                onChange={(e) => {
                                                    setCheckIn(e.target.value);

                                                    if (
                                                        checkOut &&
                                                        e.target.value > checkOut
                                                    ) {
                                                        setCheckOut("");
                                                    }
                                                }}
                                                className="h-12 w-full rounded-xl border border-slate-200 bg-[#F8FAF8] px-4 text-sm text-[#173A38] outline-none transition hover:border-[#0B5552]/30 focus:border-[#0B5552] focus:bg-white focus:ring-4 focus:ring-[#0B5552]/[0.07]"
                                            />
                                        </div>

                                        {/* Check-out */}
                                        <div>
                                            <label className="mb-2 block text-xs font-semibold text-[#173A38]">
                                                Check-out Date
                                            </label>

                                            <input
                                                type="date"
                                                value={checkOut}
                                                min={checkIn || today}
                                                onChange={(e) => setCheckOut(e.target.value)}
                                                className="h-12 w-full rounded-xl border border-slate-200 bg-[#F8FAF8] px-4 text-sm text-[#173A38] outline-none transition hover:border-[#0B5552]/30 focus:border-[#0B5552] focus:bg-white focus:ring-4 focus:ring-[#0B5552]/[0.07]"
                                            />
                                        </div>

                                    </div>
                                </div>

                                {/* Price Summary */}
                                <div className="overflow-hidden rounded-2xl border border-[#0B5552]/10 bg-[#F8FAF7]">

                                    <div className="flex items-center justify-between gap-3 border-b border-[#0B5552]/10 px-5 py-4">

                                        <div>
                                            <h4 className="text-sm font-bold text-[#173A38]">
                                                Price Summary
                                            </h4>

                                            <p className="mt-1 text-[10px] text-slate-400">
                                                Your reservation estimate
                                            </p>
                                        </div>

                                        <span className="rounded-full bg-[#E8F1EF] px-3 py-1.5 text-[10px] font-bold text-[#0B5552]">
                                            {getNights()} night(s)
                                        </span>

                                    </div>

                                    <div className="space-y-3.5 p-5 text-[13px]">

                                        <div className="flex justify-between gap-3 text-slate-500">
                                            <span>Base price / night</span>

                                            <span className="font-medium text-slate-700">
                                                {nightlyRate.toLocaleString()} AED
                                            </span>
                                        </div>

                                        <div className="flex justify-between gap-3 text-slate-500">
                                            <span>VAT (5%)</span>

                                            <span className="font-medium text-slate-700">
                                                {roundPrice(nightlyRate * 0.05).toLocaleString()} AED
                                            </span>
                                        </div>

                                        <div className="flex justify-between gap-3 text-slate-500">
                                            <span>Service charge (10%)</span>

                                            <span className="font-medium text-slate-700">
                                                {roundPrice(nightlyRate * 0.1).toLocaleString()} AED
                                            </span>
                                        </div>

                                        <div className="flex justify-between gap-3 text-slate-500">
                                            <span>Tourism fee</span>

                                            <span className="font-medium text-slate-700">
                                                {getTourismFee()} AED
                                            </span>
                                        </div>

                                        <div className="border-t border-dashed border-slate-200 pt-4">

                                            <div className="flex items-center justify-between gap-3">
                                                <span className="text-xs font-semibold text-slate-500">
                                                    Final price / night
                                                </span>

                                                <span className="text-lg font-extrabold text-[#0B5552]">
                                                    {finalNightly.toLocaleString()} AED
                                                </span>
                                            </div>

                                        </div>

                                        {checkIn && checkOut && (
                                            <div className="mt-2 flex items-center justify-between gap-3 rounded-xl bg-[#073F3D] px-4 py-4 text-white">

                                                <div>
                                                    <p className="text-xs font-semibold text-white/70">
                                                        Total stay
                                                    </p>

                                                    <p className="mt-1 text-[10px] text-white/50">
                                                        {getNights()} night(s)
                                                    </p>
                                                </div>

                                                <p className="text-xl font-extrabold">
                                                    {stayTotal.toLocaleString()} AED
                                                </p>

                                            </div>
                                        )}

                                    </div>
                                </div>

                                {/* Secure Booking */}
                                <div className="mt-4 flex gap-3 rounded-xl border border-emerald-100 bg-emerald-50/70 p-4">

                                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-emerald-100 text-emerald-700">
                                        <FaShieldAlt size={15} />
                                    </div>

                                    <div>
                                        <p className="text-xs font-bold text-emerald-800">
                                            Secure booking
                                        </p>

                                        <p className="mt-1 text-[11px] leading-5 text-emerald-800/65">
                                            Your booking request is protected. Payment is handled securely through the booking process.
                                        </p>
                                    </div>

                                </div>

                                {/* Confirm Booking */}
                                <button
                                    onClick={handleBooking}
                                    disabled={
                                        bookingLoading ||
                                        room.is_available === false
                                    }
                                    className={`mt-6 flex min-h-[52px] w-full items-center justify-center gap-2 rounded-xl px-5 py-4 text-sm font-bold transition-all duration-300 ${bookingLoading || room.is_available === false
                                        ? "cursor-not-allowed bg-slate-200 text-slate-400 shadow-none"
                                        : "bg-[#0B5552] text-white shadow-[0_8px_22px_rgba(11,85,82,0.18)] hover:-translate-y-0.5 hover:bg-[#073F3D] hover:shadow-[0_12px_28px_rgba(11,85,82,0.25)]"
                                        }`}
                                >

                                    {bookingLoading ? (
                                        <>
                                            <span className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />

                                            Processing...
                                        </>
                                    ) : (
                                        <>
                                            Confirm Booking

                                            <FaArrowRight size={14} />
                                        </>
                                    )}

                                </button>

                                <p className="mt-4 text-center text-[10px] leading-5 text-slate-400">
                                    By continuing, you agree to the applicable booking and cancellation policies.
                                </p>

                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}